import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { useGetStaticAssetUrl } from '@change-corgi/core/react/assets';
import { useI18n } from '@change-corgi/core/react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Image } from '@change-corgi/design-system-legacy/components/media';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { AccessibleFileInputWrapper } from './AccessibleFileInputWrapper';

type Props = {
	disabled?: boolean;
	onButtonClick?: () => void;
	uppy: Uppy<Meta, Body>;
	fileInputId?: string;
	buttonTheme?: 'gray';
	buttonSize?: 'small';
};
export const ImageUploader: React.FC<Props> = ({
	disabled = false,
	onButtonClick,
	uppy,
	fileInputId = 'file-input-id',
	buttonTheme,
	buttonSize,
}) => {
	const getStaticAssetUrl = useGetStaticAssetUrl();
	const DEFAULT_MEDIA_UPLOAD_IMAGE = getStaticAssetUrl('/images/image-placeholder-drop.svg');
	const { translate } = useI18n();
	const uploadAPhotoText = { strings: { chooseFiles: translate('corgi.sap.button.upload') } };

	return (
		<Box sx={{ aspectRatio: '16/9' }}>
			<Box variant="bordered" sx={{ borderStyle: 'dashed', height: '100%' }}>
				<Flex
					className="media-upload-content-wrapper"
					sx={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'column', height: '100%' }}
				>
					<Box mt={64} mb={32} sx={{ lineHeight: 0 }}>
						<Image
							data-testid="drop-target"
							src={DEFAULT_MEDIA_UPLOAD_IMAGE}
							width="115"
							alt={translate('corgi.sap.media.a11y.uploader.image_alt_text')}
						/>
					</Box>
					<Box mb={64} id="sap-media-view">
						{!disabled ? (
							<AccessibleFileInputWrapper
								uploadButtonText={uploadAPhotoText.strings.chooseFiles}
								uppy={uppy}
								fileInputId={fileInputId}
								onButtonClick={onButtonClick}
								buttonTheme={buttonTheme}
								buttonSize={buttonSize}
							/>
						) : (
							<Button disabled={disabled} data-qa="media-images-upload">
								<Text size={buttonSize === 'small' ? 'default' : 'large'}>{uploadAPhotoText.strings.chooseFiles}</Text>
							</Button>
						)}
					</Box>
				</Flex>
			</Box>
		</Box>
	);
};

ImageUploader.displayName = 'ImageUploader';
